import React, { useEffect, useMemo, useRef, useState } from 'react';
import LeftIcon from '../../assets/images/left-arw.png';
import RightIcon from '../../assets/images/right-arw.png';
import { ColoredSlug, Spinner } from '../../components/atoms';
import { useGetAppointments } from '../../hooks';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

const getCurrentWeek = () => {
  const now = new Date();
  const dayOfWeek = now.getDay() === 0 ? 6 : now.getDay() - 1;
  const startOfWeek = new Date(now.setDate(now.getDate() - dayOfWeek));
  const week = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    week.push(day);
  }
  return week;
};

const getNextWeek = (currentWeek) => {
  const startOfNextWeek = new Date(currentWeek[0]);
  startOfNextWeek.setDate(startOfNextWeek.getDate() + 7);
  return getWeekFromDate(startOfNextWeek);
};

const getPrevWeek = (currentWeek) => {
  const startOfPrevWeek = new Date(currentWeek[0]);
  startOfPrevWeek.setDate(startOfPrevWeek.getDate() - 7);
  return getWeekFromDate(startOfPrevWeek);
};

const getWeekFromDate = (date) => {
  const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1;
  const startOfWeek = new Date(date.setDate(date.getDate() - dayOfWeek));
  const week = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(startOfWeek.getDate() + i);
    week.push(day);
  }
  return week;
};

const formatDateRangeToApi = (startDate, daysForward = 6) => {
  const startTimeGteq = moment(startDate).startOf('day').toISOString();
  const startTimeLteq = moment(startDate)
    .add(daysForward, 'days')
    .endOf('day')
    .toISOString();

  return {
    startTimeGteq,
    startTimeLteq,
  };
};

export const UpcomingAppointments = () => {
  const navigate = useNavigate();
  const [currentWeek, setCurrentWeek] = useState(getCurrentWeek());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointmentData, setAppointmentData] = useState([]);
  const dayRefs = useRef({});

  const [filter, setFilter] = useState({
    filter: {
      ...formatDateRangeToApi(currentWeek[0], 6),
      nameMatches: '',
    },
    pagination: {},
  });

  const { data, refetch, loading } = useGetAppointments(filter, true);

  useEffect(() => {
    if (data?.appointments?.nodes) {
      const now = moment();
      const upcomingAppointments = data.appointments.nodes.filter(
        (appointment) =>
          moment(appointment.startTime).isSameOrAfter(now) &&
          ['rescheduled', 'accepted'].includes(appointment.status)
      );
      const sorted = upcomingAppointments.sort((a, b) =>
        moment(a.startTime).diff(moment(b.startTime))
      );
      setAppointmentData(sorted);
    }
  }, [data]);

  useEffect(() => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      filter: {
        ...prevFilter.filter,
        ...formatDateRangeToApi(currentWeek[0], 6),
      },
    }));
  }, [currentWeek]);

  useEffect(() => {
    if (selectedDate !== null) {
      refetch();
    }
  }, [filter, refetch]);

  const handleNextWeek = () => {
    const nextWeek = getNextWeek(currentWeek);
    const selectedIndex = currentWeek.findIndex(
      (d) => d.toDateString() === selectedDate.toDateString()
    );
    setCurrentWeek(nextWeek);
    setSelectedDate(nextWeek[Math.max(selectedIndex, 0)]);
  };

  const selectedWeekNumber = moment(currentWeek[0]).isoWeek();
  const currentWeekNumber = moment().isoWeek();

  const handlePrevWeek = () => {
    if (selectedWeekNumber > currentWeekNumber) {
      const prevWeek = getPrevWeek(currentWeek);
      const selectedIndex = currentWeek.findIndex(
        (d) => d.toDateString() === selectedDate.toDateString()
      );
      setCurrentWeek(prevWeek);
      setSelectedDate(prevWeek[Math.max(selectedIndex, 0)]);
    }
  };

  const handleDateClick = (date) => {
    const today = moment().startOf('day');
    if (moment(date).isSameOrAfter(today)) {
      setSelectedDate(date);
    }
  };

  const appointmentsByDay = useMemo(() => {
    const groups = {};
    appointmentData.forEach((appointment) => {
      const key = moment(appointment.startTime).format('YYYY-MM-DD');
      if (!groups[key]) groups[key] = [];
      groups[key].push(appointment);
    });
    return groups;
  }, [appointmentData]);

  useEffect(() => {
    const key = moment(selectedDate).format('YYYY-MM-DD');
    const el = dayRefs.current[key];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedDate]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <div className="bg-white-main p-4 rounded">
        <h4 className="text-black-secondary font-semibold text-base">
          Upcoming Appointments
        </h4>
        <p className="text-xs text-gray-dark mt-1">At a glance</p>
        <div className="my-4">
          <div className="calander-box mt-8 md:w-full">
            <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 sm:justify-between sm:items-center w-full">
              <p className="md:text-sm text-xs font-semibold">
                {formatDate(selectedDate)}
              </p>
              <div className="flex gap-4 self-end sm:self-auto shrink-0">
                <img
                  src={LeftIcon}
                  alt="icon"
                  className="cursor-pointer"
                  style={{
                    opacity: selectedWeekNumber <= currentWeekNumber ? 0.3 : 1,
                  }}
                  onClick={handlePrevWeek}
                />
                <img
                  src={RightIcon}
                  alt="icon"
                  className="cursor-pointer"
                  onClick={handleNextWeek}
                />
              </div>
            </div>
            <div className="calendar-date flex gap-1 items-center mt-3 overflow-x-auto pb-1">
              {currentWeek.map((day, index) => {
                const isSelected =
                  day.toDateString() === selectedDate.toDateString();
                const isPastDate = moment(day).isBefore(
                  moment().startOf('day')
                );
                return (
                  <div
                    key={index}
                    className={`grid border ${
                      isSelected
                        ? 'border-blue-bar bg-primary'
                        : 'border-purple-bdr'
                    } rounded-xl px-1 py-2 text-center cursor-pointer w-14 shrink-0 hover:border-blue-bar hover:bg-blue-calBg ${
                      isPastDate ? 'pointer-events-none opacity-50' : ''
                    }`}
                    onClick={() => handleDateClick(day)}
                  >
                    <p
                      className={`${
                        isSelected ? 'text-white-main' : 'text-purple-light'
                      } text-xs`}
                    >
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </p>
                    <p
                      className={`${
                        isSelected ? 'text-white-main' : 'text-black-secondary'
                      } font-semibold text-base`}
                    >
                      {day.getDate()}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="border-t border-blue-draft mt-5 overflow-y-auto scroll-box pb-5">
          {appointmentData?.length > 0 ? (
            currentWeek.map((day) => {
              const key = moment(day).format('YYYY-MM-DD');
              const dayAppointments = appointmentsByDay[key] || [];
              if (dayAppointments.length === 0) return null;

              return (
                <div key={key}>
                  <div
                    ref={(el) => {
                      dayRefs.current[key] = el;
                    }}
                    className="px-4 pt-4 pb-2"
                  >
                    <p className="text-xs font-semibold text-gray-dark">
                      {moment(day).format('ddd, MMM D')}
                    </p>
                  </div>
                  {dayAppointments.map((appointment) => (
                    <div
                      key={appointment.uid}
                      className="flex gap-3 items-center w-full p-4 border-b border-blue-draft cursor-pointer"
                      onClick={() =>
                        navigate('/appointment-details', { state: appointment })
                      }
                    >
                      <div className="h-12 w-12 p-1 rounded-lg">
                        <div className="aspect-w-1 aspect-h-1 rounded-lg">
                          <ColoredSlug
                            name={
                              appointment?.familyMember?.formalName ||
                              appointment?.buyer?.formalName
                            }
                          />
                        </div>
                      </div>
                      <div className="w-full">
                        <h4 className="md:text-base text-sm font-semibold">
                          {appointment?.familyMember?.formalName ||
                            appointment?.buyer?.formalName}
                        </h4>
                        <div className="flex justify-between">
                          <p className="text-gray-dark text-xs">
                            {appointment?.sellerService?.name}
                          </p>
                          <p className="text-xs text-gryt">
                            {moment(appointment?.startTime).format('hh:mm A')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })
          ) : (
            <div className="p-4 text-center text-gray-dark">
              No appointment scheduled
            </div>
          )}
        </div>
      </div>
    </>
  );
};
